import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import type { UserManager } from 'oidc-client-ts';

/**
 * Creates reauth helpers bound to a specific UserManager provider.
 *
 * Each app has its own OIDC configuration (different client IDs, redirect URIs, scopes),
 * so the UserManager is app-specific. This factory allows sharing the reauth logic
 * while keeping the UserManager provider app-specific.
 *
 * @example
 * ```ts
 * import { createReauthHelpers } from '@repo/auth/reauth';
 * import { getUserManager } from './config';
 *
 * export const { withReauth, getValidAccessToken } = createReauthHelpers(getUserManager);
 * ```
 */
export function createReauthHelpers(getUserManager: () => UserManager) {
  /**
   * Get a valid access token, attempting silent renewal if expired.
   * Returns null only if renewal fails (user must re-login).
   */
  async function getValidAccessToken(): Promise<string | null> {
    try {
      const userManager = getUserManager();
      let user = await userManager.getUser();

      if (!user) {
        return null;
      }

      if (user.expired) {
        try {
          user = await userManager.signinSilent();
        } catch {
          return null;
        }
        if (!user) {
          return null;
        }
      }

      return user.access_token;
    } catch (error) {
      console.error('[auth] Failed to get access token:', error);
      return null;
    }
  }

  /**
   * Wraps a base query with automatic token refresh on 401 responses.
   *
   * Flow:
   * 1. Get valid access token (with silent renewal if expired)
   * 2. Make the API request
   * 3. If 401 returned, attempt silent renewal and retry once
   * 4. If retry also fails, redirect to Zitadel login
   */
  function withReauth(
    createBaseQuery: (
      accessToken: string | null,
    ) => BaseQueryFn<
      string | FetchArgs,
      unknown,
      FetchBaseQueryError,
      Record<string, never>,
      FetchBaseQueryMeta
    >,
  ): BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> {
    return async (args, api, extraOptions) => {
      const accessToken = await getValidAccessToken();
      const baseQuery = createBaseQuery(accessToken);

      let result = await baseQuery(args, api, extraOptions);

      if (
        result.error &&
        'status' in result.error &&
        result.error.status === 401
      ) {
        // Token was rejected -- try silent renewal
        try {
          const userManager = getUserManager();
          const user = await userManager.signinSilent();

          if (user?.access_token) {
            // Retry with new token
            const retryBaseQuery = createBaseQuery(user.access_token);
            result = await retryBaseQuery(args, api, extraOptions);
          } else {
            // No token after renewal -- redirect to login
            redirectToLogin();
          }
        } catch {
          // Silent renewal failed -- redirect to login
          redirectToLogin();
        }
      }

      return result;
    };
  }

  function redirectToLogin() {
    const userManager = getUserManager();
    userManager.signinRedirect({
      state: { returnTo: window.location.pathname + window.location.search },
    });
  }

  return { withReauth, getValidAccessToken };
}
